import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en-gb';
import FixedTermFields from '../components/FixedTermFields';
import { isRadarSourced } from '../../../constants/employmentSourceSystems';
import UpcomingEmployeeEvents from '../components/UpcomingEmployeeEvents';
import { contractActionFacts } from '../../../utils/contractActionFacts';

dayjs.extend(utc);

/**
 * ADR-136 phase 5 (§2a settlement #8) — "Add End Date" modal content: the
 * open→fixed conversion. Same [resign.submitted, resign.finalized] batch as
 * Resign — the ADR-090 recording path — but with the A2 auto-generated
 * fixed-term sentence as the reason instead of the resignation taxonomy.
 * Honest labels over shared machinery.
 */
const EmployeeAddContractEnd = ({ employee, contract = null, episodeId = null, onValidChange }) => {
  const [formData, setFormData] = useState({
    endDate: null,
    resignationReason: ''
  });

  // ADR-187 — the acting contract's own facts (passed by ContractActionModals);
  // the person's flat fields are the PRIMARY's and are read only through the
  // resolver, when this is the primary or the payload has no contracts[].
  const facts = contract || contractActionFacts(employee, episodeId ? { id: episodeId } : null);
  const contractStartDate = facts.startDate;
  const contractStartYmd = contractStartDate ? dayjs.utc(contractStartDate).format('YYYY-MM-DD') : null;
  // ADR-146 §4.1 — the sentence keeps its "— contract <N>" suffix when the
  // contract carries a number (previously dropped: null was passed).
  const contractNumber = facts.contractNumber;
  // ADR-146 §2 — warn (never block) when the contract is owned by an
  // ingestion source: an end date the source does not carry is withdrawn by
  // its next run (the screenshot-1 revert war, prevented at the dialog).
  const syncSource = !isRadarSourced({ source: facts.source }) ? facts.source : null;
  const tomorrowYmd = dayjs().add(1, 'day').format('YYYY-MM-DD');

  const isValidEndDate = (dateStr) => {
    if (!dateStr) {
      return false;
    }
    // Strictly in the future — ending employment today or in the past is
    // Resign / Cancel territory, not a fixed-term conversion.
    if (dateStr < tomorrowYmd) {
      return false;
    }
    if (contractStartYmd && dateStr <= contractStartYmd) {
      return false;
    }
    return true;
  };

  // ADR-173 D2 — the shared FixedTermFields owns the A2 generation; validity
  // is reported whenever the values change (generation included).
  useEffect(() => {
    onValidChange(Boolean(
      formData.endDate && isValidEndDate(formData.endDate) && String(formData.resignationReason || '').trim()
    ), formData);
    // onValidChange is the modal's setter; re-running on its identity is not wanted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleEndChange = useCallback((endDate) => {
    setFormData(prev => ({ ...prev, endDate }));
  }, []);
  const handleCommentChange = useCallback((resignationReason) => {
    setFormData(prev => (prev.resignationReason === resignationReason ? prev : { ...prev, resignationReason }));
  }, []);

  const dateError = formData.endDate && !isValidEndDate(formData.endDate);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <Alert severity="info">
          This <strong>converts the contract to fixed-term</strong>: a contract end is
          scheduled on the chosen date and the person appears in the Fixed Term Contracts
          report. They remain employed until then, the end can be moved later with
          <em> Change End Date</em> or removed with <em>Resign Withdraw</em>. To end
          employment for a leaver, use <em>Resign</em> instead.
        </Alert>

        {syncSource && (
          <Alert severity="warning">
            This contract is <strong>owned by an ingestion source ({syncSource})</strong>.
            An end date that is not also recorded in the source system will be
            <strong> withdrawn by its next sync run</strong> — record it there too, or it
            will not stick.
          </Alert>
        )}

        {/* ADR-136 §15 — fetched fresh on open: the menu that offered this
            action can be stale (e.g. a replacement scheduled moments ago).
            Scoped to the acting contract when known (ADR-113 §8). */}
        <UpcomingEmployeeEvents employeeId={employee?.id} episodeId={episodeId} />

        {contractStartDate && (
          <Typography variant="body2" color="text.secondary">
            Contract started: <strong>{dayjs.utc(contractStartDate).format('DD/MM/YYYY')}</strong>
          </Typography>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FixedTermFields
              startYmd={contractStartYmd}
              endYmd={formData.endDate}
              onEndChange={handleEndChange}
              comment={formData.resignationReason}
              onCommentChange={handleCommentChange}
              contractNumber={contractNumber}
              endRequired
              minEndYmd={tomorrowYmd}
              endProblem={dateError ? 'The end date must be in the future and after the contract start' : null}
              endHelperText="The date the fixed-term contract will end"
              commentRequiredText="Required — becomes the scheduled end's reason"
              commentHelperText="Auto-generated from the term; edit freely."
            />
          </Box>
        </LocalizationProvider>

      </Box>
    </Box>
  );
};

export default EmployeeAddContractEnd;
